import { AxiosError } from "axios";
import { HttpError } from "@/core/http/HttpError";
import { useAppStore } from "@/core/store/useAppStore";
import { logger } from "@/utils/logger";
import { getPortalTokenKey, getPortalRoleKey } from "@/domains/Approval/constants/portalAuth";
import { BaseHttpClient } from "./BaseHttpClient";
import type { HttpContext } from "@/core/http/types";

class ApiClient extends BaseHttpClient {
  constructor() {
    super({ timeout: 30000, logPrefix: "ApiClient" });
    logger.info("ApiClient initialized with base URL:", this.getBaseUrl());
  }

  /**
   * HttpClient context descriptor. The admin singleton reads the active
   * client from the app store so HttpClientContext consumers see the same
   * selection as the rest of the admin UI.
   */
  get context(): HttpContext {
    const clientId = useAppStore.getState().selectedClient?.id ?? "";
    return { kind: "admin", clientId };
  }

  protected setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        logger.error("API Error:", {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          url: error.config?.url,
          baseURL: error.config?.baseURL,
        });

        if (!error.response) {
          if (error.code === "ERR_NETWORK" || error.message.includes("Network Error")) {
            throw new Error(
              "Network error: Unable to connect to the server. Please check that the API server is running."
            );
          }
          if (error.message.includes("insecure") || error.message.includes("Mixed Content")) {
            throw new Error(
              "Security error: Mixed content blocked. The API server must be accessed over HTTPS when the frontend is served over HTTPS."
            );
          }
          throw new Error(`Connection error: ${error.message}`);
        }

        const { status } = error.response;

        if (error.response?.status === 401) {
          const originalRequest = error.config;

          if (originalRequest && !(originalRequest as any)._retry) {
            (originalRequest as any)._retry = true;

            try {
              const refreshed = await this.refreshToken();
              if (refreshed) {
                const newToken = this.getAccessToken();
                if (newToken) {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  return this.client.request(originalRequest);
                }
              }
            } catch (refreshError) {
              logger.warn("Token refresh failed in interceptor");
            }
          }

          this.handleAuthError();
          throw new HttpError("Authentication required", status);
        }

        if (error.response?.status === 403) {
          throw new HttpError("Access denied. You are not authorized to use this application.", status);
        }

        if (error.response?.status === 404) {
          const apiError = this.extractErrorMessage(error.response);
          if (apiError) {
            throw new HttpError(apiError, status);
          }
          throw new HttpError("Endpoint not found. This feature may not be available.", status);
        }

        if (error.response?.status >= 500) {
          const apiError = this.extractErrorMessage(error.response);
          if (apiError) {
            logger.error("Server error details:", apiError);
            throw new HttpError(`Server error: ${apiError}`, status);
          }
          throw new HttpError("Server error. Please try again later.", status);
        }

        const apiError = this.extractErrorMessage(error.response);
        if (apiError) {
          if (this.isAuthenticationError(apiError)) {
            this.handleAuthError();
            throw new HttpError("Authentication required", status);
          }
          throw new HttpError(apiError, status);
        }

        return Promise.reject(error);
      }
    );
  }

  protected getAccessToken(): string | null {
    const { user } = useAppStore.getState();
    return user?.accessToken || null;
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const { user, setUser } = useAppStore.getState();
      if (!user?.refreshToken) return false;

      logger.info("API Client attempting token refresh...");

      const response = await fetch(`${this.getBaseUrl()}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.refreshToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();

        let newExpiresAt = user.expiresAt;
        try {
          if (data.accessToken) {
            const parts = data.accessToken.split(".");
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              if (payload.exp) {
                newExpiresAt = payload.exp * 1000;
              }
            }
          }
        } catch {
          newExpiresAt = Date.now() + 3600 * 1000;
        }

        const updatedUser = {
          ...user,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken || user.refreshToken,
          expiresAt: newExpiresAt,
        };

        setUser(updatedUser);
        logger.info("API Client token refreshed successfully");
        return true;
      } else {
        logger.warn("API Client token refresh failed");
        return false;
      }
    } catch (error) {
      logger.warn("API Client token refresh error:", error);
      return false;
    }
  }

  protected handleAuthError() {
    logger.warn("Authentication failed. Clearing session and redirecting to sign-in.");

    const currentPath = window.location.pathname;
    const portalMatch = currentPath.match(/^\/(client|editor|creator)\/([^\/]+)/);

    if (portalMatch) {
      // Admin apiClient has no business touching portal tokens. A stray
      // admin call from a portal tree (e.g. a child component that hasn't
      // been migrated to the context-resolved HttpClient yet) used to
      // clobber the creator's session on its first 401. Bail silently;
      // portal auth is owned exclusively by PortalClient + usePortalAuth.
      const [, portalType, clientId] = portalMatch;
      logger.warn(
        `Admin apiClient 401 on ${portalType} portal (client ${clientId}); leaving portal tokens intact.`
      );
      return;
    } else {
      const { logout } = useAppStore.getState();
      logout();

      if (currentPath !== "/") {
        window.location.href = "/";
      }
    }
  }

  async getBlob(url: string, config?: any): Promise<Blob> {
    const response = await this.client.get(url, {
      ...config,
      responseType: "blob",
    });
    return response.data;
  }

  async postFormData<T = any>(url: string, formData: FormData, config?: any): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  async validateToken(): Promise<boolean> {
    try {
      await this.get("/health");
      return true;
    } catch {
      return false;
    }
  }

  async extractVideoFrame(videoId: number, timestampSeconds: number): Promise<any> {
    logger.info("Extracting frame from video:", { videoId, timestampSeconds });
    const response = await this.post(`/api/admin/media/${videoId}/extract-frame`, {
      timestamp_seconds: timestampSeconds,
    });
    logger.info("Frame extracted successfully:", response);
    return response;
  }

  async streamSSE(
    url: string,
    data: any,
    onToken: (token: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void,
    signal?: AbortSignal,
    onUsage?: (usage: any) => void
  ): Promise<void> {
    const token = this.getAccessToken();
    if (!token) {
      onError(new Error("Authentication required"));
      return;
    }

    try {
      const response = await fetch(`${this.getBaseUrl()}${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
        signal,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.handleAuthError();
          onError(new Error("Authentication required"));
          return;
        }

        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          try {
            const responseClone = response.clone();
            const textBody = await responseClone.text();
            if (textBody && textBody.includes('"error"')) {
              const match = textBody.match(/"error"\s*:\s*"([^"]+)"/);
              if (match) {
                errorMessage = match[1];
              }
            }
          } catch (textError) {
            logger.error("Failed to read error response body:", textError);
          }
        }
        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Response body is null");
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              if (parsed.type === "token") {
                onToken(parsed.content);
              } else if (parsed.type === "usage") {
                if (onUsage) onUsage(parsed.data);
              } else if (parsed.type === "complete") {
                onComplete();
              } else if (parsed.type === "error") {
                onError(new Error(parsed.error));
              }
            } catch (e) {
              logger.error("Failed to parse SSE data:", e);
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        logger.info("SSE stream aborted by user");
        return;
      }
      logger.error("SSE streaming error:", error);
      onError(error instanceof Error ? error : new Error("Unknown streaming error"));
    }
  }

  async bulkUpdateStage(
    clientId: string,
    mediaItemIds: number[],
    stage: string
  ): Promise<{ success: boolean; updatedCount: number }> {
    logger.info("Bulk updating stage:", { clientId, mediaItemIds, stage });
    const response = await this.patch<{ success: boolean; updatedCount: number }>(
      `/client/${clientId}/media/bulk-stage`,
      { mediaItemIds, stage }
    );
    logger.info("Bulk stage update completed:", response);
    return response;
  }

  async bulkRotateVideos(
    clientId: string,
    mediaItemIds: number[],
    direction: "clockwise" | "counterclockwise"
  ): Promise<{ success: boolean; triggered: number; total: number }> {
    logger.info("Bulk rotating videos:", { clientId, mediaItemIds, direction });
    const response = await this.post<{ success: boolean; triggered: number; total: number }>(
      `/client/${clientId}/media/bulk-rotate-video`,
      { mediaItemIds, direction }
    );
    logger.info("Bulk rotate completed:", response);
    return response;
  }
}

export const apiClient = new ApiClient();
